import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Download, LayoutGrid, List, Search } from "lucide-react";
import { api, errMsg, brl, dt, download, LEAD_STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ScorePill, LeadStatusBadge, Sel, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function NewLead({ open, onClose, onSaved, users }) {
  const [f, setF] = useState({ name: "", phone: "", source: "manual", objective: "", interest_property_code: "", assigned_to: "" });
  const save = async () => {
    try {
      await api.post("/leads", { ...f, objective: f.objective || null, interest_property_code: f.interest_property_code || null, assigned_to: f.assigned_to || null });
      toast.success("Lead cadastrado"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-background">
        <DialogHeader><DialogTitle>Novo lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Nome"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-lead-name" /></Field>
          <Field label="Telefone (DDD)"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="11999998888" data-testid="new-lead-phone" /></Field>
          <Field label="Origem"><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="new-lead-source" /></Field>
          <Field label="Objetivo"><Sel value={f.objective} onChange={(v) => setF({ ...f, objective: v })} placeholder="—" options={{ comprar: "Comprar", alugar: "Alugar", vender: "Vender", informacoes: "Informações" }} testid="new-lead-objective" className="w-full" /></Field>
          <Field label="Imóvel (código)"><Input value={f.interest_property_code} onChange={(e) => setF({ ...f, interest_property_code: e.target.value })} data-testid="new-lead-property" /></Field>
          <Field label="Corretor"><Sel value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} placeholder="—" options={Object.fromEntries(users.map((u) => [u.id, u.name]))} testid="new-lead-broker" className="w-full" /></Field>
        </div>
        <DialogFooter><Button onClick={save} data-testid="new-lead-save">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kanban({ leads, onMove, users }) {
  const [over, setOver] = useState(null);
  const nav = useNavigate();
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
      {Object.entries(LEAD_STATUS).map(([st, label]) => {
        const col = leads.filter((l) => l.status === st);
        return (
          <div key={st} className={`kanban-col flex-1 rounded-xl border bg-secondary/40 p-3 transition-colors ${over === st ? "border-accent bg-accent/10" : ""}`} data-testid={`kanban-col-${st}`}
            onDragOver={(e) => { e.preventDefault(); setOver(st); }} onDragLeave={() => setOver(null)}
            onDrop={(e) => { setOver(null); onMove(e.dataTransfer.getData("id"), st); }}>
            <div className="flex items-center justify-between mb-3 px-1"><span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span><span className="text-xs font-mono text-muted-foreground">{col.length}</span></div>
            <div className="space-y-2 min-h-24">
              {col.map((l) => (
                <div key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("id", l.id)} onClick={() => nav(`/leads/${l.id}`)} data-testid={`kanban-card-${l.id}`}
                  className="rounded-lg border bg-card p-3 cursor-grab active:cursor-grabbing hover:border-accent/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-200 shadow-sm">
                  <div className="flex justify-between items-start gap-2"><span className="font-medium text-sm truncate">{l.name || "+" + l.phone}</span><ScorePill score={l.score} /></div>
                  <div className="text-xs text-muted-foreground mt-1">{l.interest_property_code || "—"} · {l.price_max ? brl(l.price_max) : "sem orçamento"}</div>
                  <div className="text-[10px] text-muted-foreground mt-2 flex justify-between"><span>{l.source}</span><span>{users[l.assigned_to] || "Sem corretor"}</span></div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("kanban");
  const [f, setF] = useState({ q: "", status: "", assigned_to: "", source: "" });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/leads", { params: f }).then((r) => setLeads(r.data));
  useEffect(() => { load(); }, [f]); // eslint-disable-line
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const umap = Object.fromEntries(users.map((u) => [u.id, u.name]));
  const move = async (id, status) => {
    try { await api.patch(`/leads/${id}`, { status }); toast.success(`Movido para ${LEAD_STATUS[status]}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="animate-in fade-in duration-500">
      <PageHeader title="Leads / CRM" subtitle="Funil comercial integrado às conversas do WhatsApp.">
        <Button variant="outline" onClick={() => download(`/leads/export/csv?${new URLSearchParams(f)}`, "leads.csv")} data-testid="export-csv-button"><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button variant="outline" onClick={() => download(`/leads/export/xlsx?${new URLSearchParams(f)}`, "leads.xlsx")} data-testid="export-xlsx-button"><Download className="h-4 w-4 mr-2" />XLSX</Button>
        <Button onClick={() => setOpen(true)} data-testid="new-lead-button"><Plus className="h-4 w-4 mr-2" />Novo lead</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9 w-64" placeholder="Nome, telefone, bairro, imóvel" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="leads-search-input" /></div>
        <Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="Todos os status" options={LEAD_STATUS} testid="leads-status-filter" />
        {user.role !== "broker" && <Sel value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} placeholder="Todos os corretores" options={umap} testid="leads-broker-filter" />}
        <Input className="w-40" placeholder="Origem" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="leads-source-filter" />
        <div className="ml-auto flex rounded-lg border p-0.5">
          <button onClick={() => setView("kanban")} className={`px-3 py-1.5 rounded-md text-sm ${view === "kanban" ? "bg-primary text-primary-foreground" : ""}`} data-testid="view-kanban"><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-md text-sm ${view === "list" ? "bg-primary text-primary-foreground" : ""}`} data-testid="view-list"><List className="h-4 w-4" /></button>
        </div>
      </div>
      {view === "kanban" ? <Kanban leads={leads} onMove={move} users={umap} /> : (
        <div className="card-lux p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b"><th className="p-3">Nome</th><th>Telefone</th><th>Origem</th><th>Status</th><th>Score</th><th>Imóvel</th><th>Corretor</th><th>Última interação</th></tr></thead>
            <tbody>{leads.map((l) => (
              <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)} className="border-b hover:bg-muted/40 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 font-medium">{l.name || "—"}</td><td className="font-mono text-xs">+{l.phone}</td><td>{l.source}</td><td><LeadStatusBadge status={l.status} /></td>
                <td><ScorePill score={l.score} /></td><td className="font-mono text-xs">{l.interest_property_code || "—"}</td><td>{umap[l.assigned_to] || "—"}</td><td className="text-xs">{dt(l.last_interaction_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <NewLead open={open} onClose={() => setOpen(false)} onSaved={load} users={users} />
    </div>
  );
}
